import type {
  AcceptManyResult,
  CursorPage,
  DriverApplyInput,
  DriverStatsDto,
  DriverRouteDto,
  MeDto,
  OrderDto,
} from '@posilka/shared';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { meKey } from './me';
import { orderKeys } from './orders';

export interface AvailableFilters {
  fromCityId?: string;
  toCityId?: string;
  date?: string;
}

export const driverKeys = {
  available: (f: AvailableFilters) => ['driver', 'available', f] as const,
  active: ['driver', 'active'] as const,
  routes: ['driver', 'routes'] as const,
};

export const useAvailableOrders = (filters: AvailableFilters, enabled: boolean) =>
  useInfiniteQuery({
    queryKey: driverKeys.available(filters),
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams(
        Object.entries({ ...filters, cursor: pageParam }).filter(([, v]) => !!v) as [
          string,
          string,
        ][],
      );
      return api<CursorPage<OrderDto>>(`/driver/orders/available?${params}`);
    },
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled,
    refetchInterval: 30_000,
  });

export const useActiveDeliveries = () =>
  useQuery({
    queryKey: driverKeys.active,
    queryFn: () => api<OrderDto[]>('/driver/orders/active'),
  });

export const useDriverRoutes = (enabled: boolean) =>
  useQuery({
    queryKey: driverKeys.routes,
    queryFn: () => api<DriverRouteDto[]>('/driver/routes'),
    enabled,
  });

export const useSetRoutes = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (routes: DriverRouteDto[]) =>
      api<DriverRouteDto[]>('/driver/routes', { method: 'PUT', body: { routes } }),
    onSuccess: (routes) => qc.setQueryData(driverKeys.routes, routes),
  });
};

export const useApplyDriver = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DriverApplyInput) => api<MeDto>('/driver/apply', { method: 'POST', body }),
    onSuccess: (me) => qc.setQueryData(meKey, me),
  });
};

export const pickupOrder = (id: string, pickupPhotoKey: string) =>
  api<OrderDto>(`/orders/${id}/pickup`, { method: 'POST', body: { pickupPhotoKey } });

export const deliverOrder = (id: string, code: string, deliveryPhotoKey: string) =>
  api<OrderDto>(`/orders/${id}/deliver`, { method: 'POST', body: { code, deliveryPhotoKey } });

export const invalidateOrder = (qc: ReturnType<typeof useQueryClient>, order: OrderDto) => {
  qc.setQueryData(orderKeys.detail(order.id), order);
  void qc.invalidateQueries({ queryKey: ['driver'] });
  void qc.invalidateQueries({ queryKey: orderKeys.all, refetchType: 'none' });
};

export const acceptManyOrders = (ids: string[]) =>
  api<AcceptManyResult>('/driver/orders/accept-many', { method: 'POST', body: { ids } });

export const useDriverStats = (enabled = true) =>
  useQuery({
    queryKey: ['driver', 'stats'],
    queryFn: () => api<DriverStatsDto>('/driver/stats'),
    enabled,
    staleTime: 60_000,
  });
